<template>
  <component
    :is="isRevealed ? 'a' : 'span'"
    class="maskedLink masked-link"
    :class="{ underlined: withUnderline }"
    :href="isRevealed && redirectTo ? redirectTo : null"
    :target="isRevealed ? target : null"
    :rel="
      isRevealed && target === '_blank'
        ? 'noopener noreferrer'
        : isRevealed
        ? rel
        : null
    "
    :tabindex="0"
    role="link"
    @mouseover="revealLink"
    @click="handleClick"
    @auxclick="handleAuxClick"
    @keydown.enter="handleEnter"
  >
    <slot>{{ text }}</slot>
  </component>
</template>

<script>
export default {
  name: 'MaskedLink',
  props: {
    text: {
      type: String,
      default: '',
    },
    redirectTo: {
      type: String,
      required: true,
    },
    target: {
      type: String,
      default: '_self',
    },
    rel: {
      type: String,
      default: '',
    },
    withUnderline: {
      type: Boolean,
      default: false,
    },
    // Called on click, middle-click and Enter (e.g: for tracking). If it returns
    // a promise, same-tab navigation waits for it; new-tab navigation stays native.
    beforeNavigate: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      isRevealed: false,
    };
  },
  methods: {
    revealLink() {
      if (this.redirectTo) {
        this.isRevealed = true;
      }
    },
    isSameTab(event) {
      if (!this.redirectTo || this.target === '_blank') {
        return false;
      }

      // A masked span always navigates in the same tab (see navigate()).
      return (
        !this.isRevealed || !(event.ctrlKey || event.metaKey || event.shiftKey)
      );
    },
    callBeforeNavigate(event) {
      const result = this.beforeNavigate && this.beforeNavigate(event);
      // Tracking must never block or break navigation, so swallow failures.
      return result && Promise.resolve(result).catch(() => {});
    },
    handleClick(event) {
      const pending = this.callBeforeNavigate(event);

      if (pending && this.isSameTab(event)) {
        event.preventDefault();
        pending.then(this.navigate);
      } else if (!this.isRevealed) {
        this.navigate();
      }
    },
    handleEnter(event) {
      // On a revealed anchor, Enter fires a native click handled by handleClick.
      if (!this.isRevealed) {
        this.handleClick(event);
      }
    },
    handleAuxClick(event) {
      // Only middle-click opens the link. Right-click fires auxclick too, but
      // we can't know whether the context menu ends in an actual opening.
      if (event.button === 1) {
        this.callBeforeNavigate(event);
      }
    },
    navigate() {
      const redirectTo = this.redirectTo;

      if (!redirectTo) {
        return;
      }

      if (this.target === '_blank') {
        window.open(redirectTo, '_blank');
      } else {
        location.assign(redirectTo);
      }
    },
  },
};
</script>

<style scoped lang="scss">
span {
  cursor: pointer;
}

.underlined {
  text-decoration: underline;
}
</style>
